import React, { useState } from 'react';
import BelagomProfilePhoto from '../components/BelagomProfilePhoto';
import { ComplianceTierMedaille, isMedailleTier } from '../components/ComplianceTierMedaille';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, Button } from '../components';
import {
  Star, MapPin, Clock, Languages,
  Phone, Mail, Briefcase, GraduationCap,
  Globe, ArrowLeft, Heart, MessageSquare, X, Download
} from 'lucide-react';
import { useProfessionalDetail } from '../hooks/useProfessionalDetail';
import { useProfileTaxonomy, useTaxonomyLabel, type TaxonomyOption } from '../hooks/useProfileTaxonomy';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import toast from 'react-hot-toast';

interface WorkExperience {
  company: string;
  position: string;
  start_date: string;
  end_date?: string;
  current: boolean;
  description: string;
}

interface Education {
  institution: string;
  degree: string;
  field: string;
  start_year: number;
  end_year?: number;
  current: boolean;
}

export const ProfessionalDetailPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const { t: tProfile } = useTranslation('profile');
  const { data: taxonomy } = useProfileTaxonomy();
  const label = useTaxonomyLabel();
  /** Toont het label uit de taxonomie; valt terug op de ruwe waarde */
  const taxonomyLabel = (options: TaxonomyOption[] | undefined, value: string) => {
    const option = options?.find((o) => o.value === value);
    return option ? label(option) : value;
  };
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isSaved, setIsSaved] = useState(false);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const { user } = useAuthStore();

  const { data: professional, isLoading, error } = useProfessionalDetail(id!);

  const { data: reviewsSummary } = useQuery({
    queryKey: ['professional-reviews-summary', id],
    queryFn: async () => {
      const response = await api.get(`/employer/professional/${id}/reviews-summary`);
      return response.data.data;
    },
    enabled: !!id && !!user && user.role === 'opdrachtgever' && showReviewsModal
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !professional) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card>
          <div className="p-8 text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('professionalDetail.notFoundTitle')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('professionalDetail.notFoundDesc')}
            </p>
            <Button onClick={() => navigate(-1)}>
              {t('professionalDetail.back')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const handleSave = () => {
    setIsSaved(!isSaved);
  };

  const handleDownloadPdf = async () => {
    if (!id) return;
    setDownloadingPdf(true);
    try {
      const response = await api.get(`/employer/professional/${id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Profiel-${professional?.first_name}-${professional?.last_name}-BeLagom.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('professionalDetail.toastDownloaded'));
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(t('professionalDetail.toastDownloadError'));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleContact = () => {
    const subject = encodeURIComponent(`BeLagom - Interesse in jouw diensten als ${professional.professional_type.replace('_', ' ')}`);
    const body = encodeURIComponent(`Beste ${professional.first_name},\n\nIk heb jouw profiel gezien op BeLagom en ben geïnteresseerd in jouw diensten.\n\nGraag zou ik een kennismakingsgesprek plannen.\n\nMet vriendelijke groet,`);
    window.open(`mailto:${professional.email}?subject=${subject}&body=${body}`);
  };

  const handleStartProject = () => {
    navigate('/opdrachten/nieuw', {
      replace: false,
      state: {
        selectedProfessional: {
          id: professional.user_id || professional.id,
          name: `${professional.first_name} ${professional.last_name}`,
          type: professional.professional_type
          // Tarief komt pas in de onderhandelingsfase in beeld (FO)
        }
      }
    });
  };

  const handleStartConversation = async () => {
    if (!user || !professional) return;
    try {
      if (user.role === 'opdrachtgever') {
        const conversationResponse = await api.post('/communication/conversations/professional-employer', {
          professional_id: professional.user_id || id
        });
        if (conversationResponse.data.success) {
          toast.success(t('professionalDetail.toastChatStarted'));
          navigate(`/berichten?conversation=${conversationResponse.data.data.id}`);
        }
      } else {
        toast.error(t('professionalDetail.toastEmployerOnly'));
      }
    } catch (error: any) {
      console.error('Error starting conversation:', error);
      if (error?.response?.status === 400 || error?.response?.status === 409) {
        navigate('/berichten');
      } else {
        toast.error(t('professionalDetail.toastChatError'));
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long'
    });
  };

  const getProfessionalTypeLabel = (type: string) => {
    const types: { [key: string]: string } = {
      'bedrijfsmaatschappelijk_werker': 'Bedrijfsmaatschappelijk Werker',
      'vertrouwenspersoon': 'Vertrouwenspersoon',
      'mediator': 'Mediator'
    };
    return types[type] || type;
  };

  const getAvailabilityText = () => {
    if (professional.availability_status === 'immediately') {
      return t('professionalDetail.availImmediately');
    } else if (professional.availability_status === 'specific_date' && professional.available_from_date) {
      return t('professionalDetail.availFrom', { date: formatDate(professional.available_from_date) });
    }
    return t('professionalDetail.notAvailable');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('professionalDetail.back')}
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Profile */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Card */}
            <Card>
              <div className="p-6">
                <div className="flex items-start space-x-6">
                  <BelagomProfilePhoto
                    src={professional.profile_photo_path}
                    alt={`${professional.first_name} ${professional.last_name}`}
                    size={96}
                  />

                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h1 className="text-3xl font-bold text-gray-900">
                          {professional.first_name} {professional.last_name}
                        </h1>
                        {professional.company_name && (
                          <p className="text-lg font-medium text-gray-800">{professional.company_name}</p>
                        )}
                        <p className="text-xl text-blue-600 font-medium mt-1">
                          {getProfessionalTypeLabel(professional.professional_type)}
                        </p>
                      </div>

                      {professional.company_logo_url && (
                        <img
                          src={professional.company_logo_url.startsWith('/') ? professional.company_logo_url : `/uploads/${professional.company_logo_url}`}
                          alt=""
                          className="ml-4 max-h-14 max-w-[160px] object-contain"
                        />
                      )}

                      {isMedailleTier(professional.compliance_tier) && professional.compliance_indicatoren && (
                        <ComplianceTierMedaille
                          tier={professional.compliance_tier}
                          indicatoren={professional.compliance_indicatoren}
                          size={88}
                          className="ml-4"
                        />
                      )}

                      <button
                        onClick={handleSave}
                        className={`p-3 rounded-full ${
                          isSaved
                            ? 'text-red-500 bg-red-50'
                            : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                        } transition-colors`}
                      >
                        <Heart className={`w-6 h-6 ${isSaved ? 'fill-current' : ''}`} />
                      </button>
                    </div>

                    {/* Rating */}
                    {(professional.average_rating || professional.total_reviews) && (
                      <div className="flex items-center mt-3">
                        <button
                          type="button"
                          onClick={() => user?.role === 'opdrachtgever' && (professional.total_reviews || 0) > 0 && setShowReviewsModal(true)}
                          className={`flex items-center ${user?.role === 'opdrachtgever' && (professional.total_reviews || 0) > 0 ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                        >
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-5 h-5 ${
                                  i < Math.floor(professional.average_rating || 0)
                                    ? 'text-yellow-400 fill-current'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="ml-3 text-lg font-medium text-gray-900">
                            {typeof professional.average_rating === 'number'
                              ? professional.average_rating.toFixed(1)
                              : parseFloat(String(professional.average_rating || 0)).toFixed(1)}
                          </span>
                          <span className="ml-2 text-gray-600">
                            ({professional.total_reviews || 0} {t('professionalDetail.review', { count: professional.total_reviews || 0 })})
                          </span>
                        </button>
                      </div>
                    )}

                    {/* Location & Basic Info */}
                    <div className="grid md:grid-cols-2 gap-6 mt-6">
                      <div className="space-y-3">
                        <div className="flex items-center text-gray-700">
                          <MapPin className="w-5 h-5 mr-3 text-gray-400" />
                          <span>{professional.city}</span>
                        </div>

                        <div className="flex items-center text-gray-700">
                          <Clock className="w-5 h-5 mr-3 text-gray-400" />
                          <span>{getAvailabilityText()}</span>
                        </div>

                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center text-gray-700">
                          <Languages className="w-5 h-5 mr-3 text-gray-400" />
                          <span>{professional.languages.join(', ')}</span>
                        </div>

                        <div className="flex items-center text-gray-700">
                          <Globe className="w-5 h-5 mr-3 text-gray-400" />
                          <span>
                            {professional.reisradius_km
                              ? `${professional.reisradius_km} km ${t('professionalDetail.aroundOffice', { defaultValue: 'rond kantoor' })}`
                              : t('professionalDetail.wholeCountry', { defaultValue: 'Heel Nederland' })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Intro Video */}
            {professional.intro_video_path && (
              <Card>
                <div className="p-6">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                    {t('professionalDetail.introTitle')}
                  </h2>
                  <video
                    src={professional.intro_video_path.startsWith('/') ? professional.intro_video_path : `/uploads/${professional.intro_video_path}`}
                    controls
                    className="w-full rounded-lg"
                    style={{ maxHeight: '400px' }}
                  />
                </div>
              </Card>
            )}

            {/* Experience & Qualifications */}
            <Card>
              <div className="p-6">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                  {t('professionalDetail.expTitle')}
                </h2>

                {/* Industry Experience */}
                {professional.industry_experience && professional.industry_experience.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      {t('professionalDetail.affinityTitle')}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {professional.industry_experience.map((industry: string) => (
                        <span
                          key={industry}
                          className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full"
                        >
                          {industry}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Specialisme-tags (FO stap 4) — vervangt de provincie-lijst */}
                {professional.specialisatie_tags && professional.specialisatie_tags.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      {tProfile('label.specialisations').replace(' *', '')}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {professional.specialisatie_tags.map((tag: string) => (
                        <span key={tag} className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full">
                          {taxonomyLabel(taxonomy?.specialisatie_tags, tag)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Doelgroep-niveaus (FO stap 4) */}
                {professional.doelgroep_niveaus && professional.doelgroep_niveaus.length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      {tProfile('label.targetGroups').replace(' *', '')}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {professional.doelgroep_niveaus.map((niveau: string) => (
                        <span key={niveau} className="px-3 py-1 bg-green-50 text-green-700 text-sm rounded-full">
                          {taxonomyLabel(taxonomy?.doelgroep_niveaus, niveau)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Work Experience */}
            {professional.work_experience && professional.work_experience.length > 0 && (
              <Card>
                <div className="p-6">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                    <Briefcase className="w-6 h-6 mr-3" />
                    {t('professionalDetail.workExpTitle')}
                  </h2>

                  <div className="space-y-6">
                    {professional.work_experience.map((work: WorkExperience, index: number) => (
                      <div key={index} className="border-l-4 border-blue-200 pl-6">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {work.position}
                        </h3>
                        <p className="text-blue-600 font-medium">{work.company}</p>
                        <p className="text-sm text-gray-500 mb-3">
                          {formatDate(work.start_date)} - {
                            work.current ? t('professionalDetail.workPresent') : work.end_date ? formatDate(work.end_date) : t('professionalDetail.workUnknown')
                          }
                        </p>
                        <p className="text-gray-700">{work.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            )}

            {/* Employer References */}
            {professional.employer_references && professional.employer_references.length > 0 && (
              <Card>
                <div className="p-6">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                    <Briefcase className="w-6 h-6 mr-3" />
                    {t('professionalDetail.employersTitle')}
                  </h2>
                  <p className="text-sm text-gray-500 mb-4">
                    {t('professionalDetail.employersDesc')}
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {professional.employer_references.map((ref: any, index: number) => (
                      <div key={index} className="flex items-center p-3 border border-gray-200 rounded-lg">
                        {ref.logo_url ? (
                          <img src={ref.logo_url} alt={ref.company_name} className="w-10 h-10 rounded-full object-cover mr-3" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                            <Briefcase className="w-5 h-5 text-blue-600" />
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-gray-900">{ref.company_name || t('professionalDetail.employerDefault')}</p>
                          <p className="text-xs text-gray-500">
                            {ref.status === 'active' ? t('professionalDetail.employerActive') : t('professionalDetail.employerCompleted')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            )}

            {/* Education */}
            {professional.education && professional.education.length > 0 && (
              <Card>
                <div className="p-6">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                    <GraduationCap className="w-6 h-6 mr-3" />
                    {t('professionalDetail.eduTitle')}
                  </h2>

                  <div className="space-y-6">
                    {professional.education.map((edu: Education, index: number) => (
                      <div key={index} className="border-l-4 border-green-200 pl-6">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {edu.degree}
                        </h3>
                        <p className="text-green-600 font-medium">{edu.institution}</p>
                        <p className="text-gray-700">{edu.field}</p>
                        <p className="text-sm text-gray-500">
                          {edu.start_year} - {
                            edu.current ? t('professionalDetail.eduCurrent') : edu.end_year || t('professionalDetail.workUnknown')
                          }
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-8">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">
                  {t('professionalDetail.sidebarTitle')}
                </h3>

                <div className="space-y-4">
                  <Button
                    onClick={handleContact}
                    className="w-full flex items-center justify-center"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {t('professionalDetail.contactEmail')}
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => window.open(`tel:${professional.phone}`, '_self')}
                    className="w-full flex items-center justify-center"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    {t('professionalDetail.call')}
                  </Button>

                  {user?.role === 'opdrachtgever' && (
                    <Button
                      onClick={handleStartConversation}
                      className="w-full flex items-center justify-center bg-blue-600 hover:bg-blue-700"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      {t('professionalDetail.startChat')}
                    </Button>
                  )}

                  <Button
                    type="button"
                    onClick={handleStartProject}
                    className="w-full flex items-center justify-center bg-green-600 hover:bg-green-700"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    {t('professionalDetail.startProject')}
                  </Button>

                  {user?.role === 'opdrachtgever' && (
                    <Button
                      variant="outline"
                      onClick={handleDownloadPdf}
                      disabled={downloadingPdf}
                      className="w-full flex items-center justify-center"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {downloadingPdf ? t('professionalDetail.downloading') : t('professionalDetail.downloadPdf')}
                    </Button>
                  )}
                </div>

                {/* Professional Status */}
                <div className="mt-6 p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-sm font-medium text-green-800">
                      {t('professionalDetail.verifiedTitle')}
                    </span>
                  </div>
                  <p className="text-xs text-green-600 mt-1">
                    {t('professionalDetail.verifiedDesc')}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {showReviewsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('professionalDetail.reviewsModal', { name: `${professional.first_name} ${professional.last_name}` })}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t('professionalDetail.reviewsModalDesc')}
                  </p>
                </div>
                <button
                  onClick={() => setShowReviewsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {reviewsSummary ? (
                <>
                  <div className="flex items-center gap-2 mb-6">
                    <div className="flex items-center">
                      <Star className="w-6 h-6 fill-amber-500 text-amber-500" />
                      <span className="ml-1 text-xl font-semibold text-gray-900">
                        {reviewsSummary.average_rating?.toFixed(1) ?? '-'}
                      </span>
                    </div>
                    <span className="text-sm text-gray-500">
                      {t('professionalDetail.reviewsAvgBasis')} {t('professionalDetail.review', { count: reviewsSummary.review_count ?? 0 })}
                    </span>
                  </div>
                  {reviewsSummary.questions?.length > 0 && (
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-gray-700">{t('professionalDetail.reviewBreakdown')}</h4>
                      {reviewsSummary.questions.map((q: any, i: number) => (
                        <div key={i} className="border-b border-gray-100 pb-3 last:border-0">
                          <p className="text-sm font-medium text-gray-800">{q.label}</p>
                          <p className="text-xs text-gray-600 mb-1">{q.question}</p>
                          <div className="flex items-center gap-2">
                            <div className="flex gap-0.5">
                              {[1, 2, 3, 4, 5].map((v) => (
                                <Star
                                  key={v}
                                  className={`w-4 h-4 ${
                                    (q.avg ?? 0) >= v ? 'fill-amber-400 text-amber-400' : 'text-gray-200'
                                  }`}
                                />
                              ))}
                            </div>
                            <span className="text-sm text-gray-600">
                              {(q.avg ?? 0) > 0 ? (q.avg ?? 0).toFixed(1) : '-'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="py-8 text-center text-gray-500">
                  {t('professionalDetail.reviewsLoading')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
